import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BrandingSettings = () => {
  const [formData, setFormData] = useState({
    primaryColor: '#3B82F6',
    secondaryColor: '#EF4444',
    accentColor: '#10B981',
    logoUrl: '',
    faviconUrl: '',
    brandName: 'AccounTech AI',
    tagline: 'Smart Financial Management',
    fontFamily: 'Inter',
    borderRadius: '8'
  });

  const [files, setFiles] = useState({
    logo: null,
    favicon: null,
    letterhead: null
  });

  const colorPresets = [
    { name: 'Blue', primary: '#3B82F6', secondary: '#1E40AF' },
    { name: 'Green', primary: '#10B981', secondary: '#047857' },
    { name: 'Purple', primary: '#8B5CF6', secondary: '#7C3AED' },
    { name: 'Red', primary: '#EF4444', secondary: '#DC2626' },
    { name: 'Orange', primary: '#F59E0B', secondary: '#D97706' },
    { name: 'Pink', primary: '#EC4899', secondary: '#DB2777' }
  ];

  const fontOptions = [
    'Inter',
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Poppins'
  ];

  const handleColorChange = (field, color) => {
    setFormData(prev => ({
      ...prev,
      [field]: color
    }));
  };

  const handleFileUpload = (type, file) => {
    setFiles(prev => ({
      ...prev,
      [type]: file
    }));
  };

  const applyColorPreset = (preset) => {
    setFormData(prev => ({
      ...prev,
      primaryColor: preset?.primary,
      secondaryColor: preset?.secondary
    }));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Branding Settings</h2>
          <p className="text-muted-foreground">Customize your organization's visual identity and brand elements</p>
        </div>
        <Button iconName="Save">
          Save Changes
        </Button>
      </div>
      {/* Color Scheme */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Color Scheme</h3>
        
        {/* Color Presets */}
        <div className="mb-6">
          <label className="text-sm font-medium text-foreground mb-3 block">
            Quick Color Presets
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {colorPresets?.map((preset) => (
              <button
                key={preset?.name}
                onClick={() => applyColorPreset(preset)}
                className="p-3 rounded-lg border border-border hover:border-primary transition-colors"
              >
                <div className="flex space-x-2 mb-2">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: preset?.primary }}
                  />
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: preset?.secondary }}
                  />
                </div>
                <span className="text-xs font-medium text-foreground">{preset?.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Colors */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Primary Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData?.primaryColor}
                onChange={(e) => handleColorChange('primaryColor', e?.target?.value)}
                className="w-12 h-10 rounded-md border border-input cursor-pointer"
              />
              <Input
                value={formData?.primaryColor}
                onChange={(e) => handleColorChange('primaryColor', e?.target?.value)}
                placeholder="#3B82F6"
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Secondary Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData?.secondaryColor}
                onChange={(e) => handleColorChange('secondaryColor', e?.target?.value)}
                className="w-12 h-10 rounded-md border border-input cursor-pointer"
              />
              <Input
                value={formData?.secondaryColor}
                onChange={(e) => handleColorChange('secondaryColor', e?.target?.value)}
                placeholder="#EF4444"
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Accent Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData?.accentColor}
                onChange={(e) => handleColorChange('accentColor', e?.target?.value)}
                className="w-12 h-10 rounded-md border border-input cursor-pointer"
              />
              <Input
                value={formData?.accentColor}
                onChange={(e) => handleColorChange('accentColor', e?.target?.value)}
                placeholder="#10B981"
                className="flex-1"
              />
            </div>
          </div>
        </div>

        {/* Color Preview */}
        <div className="mt-6 p-4 rounded-lg border border-border">
          <p className="text-sm font-medium text-foreground mb-3">Color Preview</p>
          <div className="flex items-center space-x-4">
            <Button style={{ backgroundColor: formData?.primaryColor }} className="text-white">
              Primary Button
            </Button>
            <Button 
              variant="outline" 
              style={{ borderColor: formData?.secondaryColor, color: formData?.secondaryColor }}
            >
              Secondary Button
            </Button>
            <div 
              className="px-3 py-2 rounded-md text-white text-sm"
              style={{ backgroundColor: formData?.accentColor }}
            >
              Accent Color
            </div>
          </div>
        </div>
      </div>
      {/* Typography */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Typography</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Font Family
            </label>
            <select
              value={formData?.fontFamily}
              onChange={(e) => setFormData(prev => ({ ...prev, fontFamily: e?.target?.value }))}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {fontOptions?.map((font) => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Border Radius (px)
            </label>
            <Input
              type="number"
              value={formData?.borderRadius}
              onChange={(e) => setFormData(prev => ({ ...prev, borderRadius: e?.target?.value }))}
              placeholder="8"
              min="0"
              max="20"
            />
          </div>
        </div>

        {/* Typography Preview */}
        <div className="mt-6 p-4 rounded-lg border border-border">
          <p className="text-sm font-medium text-foreground mb-3">Typography Preview</p>
          <div style={{ fontFamily: formData?.fontFamily }}>
            <h4 className="text-lg font-semibold text-foreground mb-2">Heading Example</h4>
            <p className="text-muted-foreground mb-2">
              This is an example of body text using the selected font family.
            </p>
            <Button 
              style={{ 
                borderRadius: `${formData?.borderRadius}px`,
                backgroundColor: formData?.primaryColor 
              }}
              className="text-white"
            >
              Button with Custom Radius
            </Button>
          </div>
        </div>
      </div>
      {/* Brand Assets */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Brand Assets</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Logo Upload */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Logo
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
              <Icon name="Upload" size={24} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Upload your logo
              </p>
              <p className="text-xs text-muted-foreground">
                PNG, JPG up to 2MB
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload('logo', e?.target?.files?.[0])}
                className="hidden"
              />
            </div>
          </div>

          {/* Favicon Upload */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Favicon
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
              <Icon name="Upload" size={24} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Upload favicon
              </p>
              <p className="text-xs text-muted-foreground">
                ICO, PNG 32x32px
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload('favicon', e?.target?.files?.[0])}
                className="hidden"
              />
            </div>
          </div>

          {/* Letterhead Upload */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Letterhead Template
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
              <Icon name="Upload" size={24} className="mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-2">
                Upload letterhead
              </p>
              <p className="text-xs text-muted-foreground">
                PDF, PNG up to 5MB
              </p>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileUpload('letterhead', e?.target?.files?.[0])}
                className="hidden"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Brand Text */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Brand Text</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Brand Name"
            value={formData?.brandName}
            onChange={(e) => setFormData(prev => ({ ...prev, brandName: e?.target?.value }))}
            placeholder="Your Brand Name"
          />
          <Input
            label="Tagline"
            value={formData?.tagline}
            onChange={(e) => setFormData(prev => ({ ...prev, tagline: e?.target?.value }))}
            placeholder="Your Brand Tagline"
          />
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-6 border-t border-border">
        <Button variant="outline">
          Reset to Default
        </Button>
        <Button iconName="Save">
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default BrandingSettings;